import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { QuestionType } from '../../../../generated/hooks';
import { isMultipleChoice, type CustomMetricSummary } from '@arnold/core';
import { inferQuestionScoring } from '../../questionScoring';
import type { ReportQuestion } from '../../types';
import { formatMetricNumber } from '../formatMetricNumber';
import {
  localizedText,
  newDefinitionId,
  type MetricCalculation,
  type NpsBucket,
  type ReportMetricDefinition,
  type RespondentCondition,
} from '../types';
import { emptySource, makeNpsSource } from './sourceFactories';
import { Action, Bar, Check, Field, FormGrid, Hint, InlineAdd, SourceBlock } from './styles';

type SegmentCalculation = Extract<MetricCalculation, { type: 'respondentSegment' }>;
const BUCKETS: NpsBucket[] = ['detractor', 'passive', 'promoter'];

const newDeviationCondition = (t: TFunction, language: string): RespondentCondition => ({
  kind: 'belowDeviation',
  id: newDefinitionId(),
  label: { [language]: t('definition.segment.newCategory') },
  sources: [],
  deviations: 1,
});
const newNpsCondition = (question: ReportQuestion | undefined): RespondentCondition => ({
  kind: 'npsBucket',
  id: newDefinitionId(),
  ...(question ? makeNpsSource(question) : { source: { ...emptySource(), max: 10 }, inputMin: 0 as const }),
  buckets: ['detractor'],
});

/**
 * Conditions of a `respondentSegment` metric: scale categories a respondent must fall below (mean − k·SD)
 * and NPS buckets they must belong to. Shows the report-wide thresholds the live preview calculated.
 */
export function RespondentSegmentEditor({
  calculation,
  metrics,
  questions,
  language,
  summary,
  onChange,
}: {
  calculation: SegmentCalculation;
  /** `scaleTotal` metrics whose questions a category can include. */
  metrics: ReportMetricDefinition[];
  questions: ReportQuestion[];
  language: string;
  summary?: CustomMetricSummary;
  onChange: (calculation: MetricCalculation) => void;
}) {
  const { t, i18n } = useTranslation('newReport');
  const npsQuestions = questions.filter(
    (question) => [QuestionType.Select].includes(question.type) && !isMultipleChoice(question),
  );
  const setConditions = (conditions: RespondentCondition[]) => onChange({ ...calculation, conditions });
  const updateCondition = (index: number, condition: RespondentCondition) =>
    setConditions(calculation.conditions.map((item, i) => (i === index ? condition : item)));
  const scaleNumber = (value: number | undefined) =>
    value === undefined ? '—' : formatMetricNumber(value, i18n.language, 'scale');
  const toggle = <T,>(list: T[], item: T, checked: boolean) =>
    checked ? [...list, item] : list.filter((value) => value !== item);

  return (
    <>
      <Hint>{t('definition.segment.conditionsHelp')}</Hint>
      {calculation.conditions.map((condition, index) => {
        const threshold = summary?.thresholds?.find((item) => item.id === condition.id);
        return (
          <SourceBlock key={condition.id}>
            <InlineAdd>
              <strong>
                {index + 1}. {t(`definition.segment.kinds.${condition.kind}`)}
              </strong>
              {calculation.conditions.length > 1 && (
                <Action
                  title={t('definition.segment.removeCondition')}
                  onClick={() => setConditions(calculation.conditions.filter((_, i) => i !== index))}
                >
                  ×
                </Action>
              )}
            </InlineAdd>
            {condition.kind === 'belowDeviation' ? (
              <>
                <FormGrid>
                  <Field>
                    {t('definition.segment.categoryName')} ({language})
                    <input
                      value={condition.label[language] ?? ''}
                      onChange={(event) =>
                        updateCondition(index, {
                          ...condition,
                          label: { ...condition.label, [language]: event.target.value },
                        })
                      }
                    />
                  </Field>
                  <Field>
                    {t('definition.segment.deviations')}
                    <input
                      type="number"
                      step="any"
                      min={0}
                      value={condition.deviations}
                      onChange={(event) =>
                        updateCondition(index, { ...condition, deviations: Number(event.target.value) })
                      }
                    />
                  </Field>
                </FormGrid>
                {metrics.length > 0 && (
                  <>
                    <Hint>{t('definition.segment.includeMetrics')}</Hint>
                    {metrics.map((metric) => (
                      <Check key={metric.id}>
                        <input
                          type="checkbox"
                          checked={condition.metricIds?.includes(metric.id) ?? false}
                          onChange={(event) =>
                            updateCondition(index, {
                              ...condition,
                              metricIds: toggle(condition.metricIds ?? [], metric.id, event.target.checked),
                            })
                          }
                        />
                        {localizedText(metric.title, language) || t('definition.newMetric')}
                      </Check>
                    ))}
                  </>
                )}
                <Hint>
                  {threshold
                    ? t('definition.segment.threshold', {
                        mean: scaleNumber(threshold.mean),
                        deviation: scaleNumber(threshold.deviation),
                        bound: scaleNumber(threshold.bound),
                        count: threshold.population,
                      })
                    : t('definition.metricNoData')}
                </Hint>
              </>
            ) : (
              <>
                <FormGrid>
                  <Field>
                    {t('definition.question')}
                    <select
                      value={condition.source.questionIndex}
                      onChange={(event) => {
                        const question = npsQuestions.find((item) => item.index === event.target.value);
                        if (question) updateCondition(index, { ...condition, ...makeNpsSource(question) });
                      }}
                    >
                      <option value="">{t('definition.chooseQuestion')}</option>
                      {npsQuestions.map((question) => (
                        <option key={question.index} value={question.index}>
                          {question.text}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field>
                    {t('definition.npsRange')}
                    <select
                      value={condition.inputMin}
                      onChange={(event) => {
                        const inputMin = Number(event.target.value) as 0 | 1;
                        const question = questions.find((item) => item.index === condition.source.questionIndex);
                        const length = 11 - inputMin;
                        updateCondition(index, {
                          ...condition,
                          inputMin,
                          source: {
                            ...condition.source,
                            min: inputMin,
                            max: 10,
                            optionScores: (question?.answersLocal ?? []).map((_, i) =>
                              i >= length ? null : i + inputMin,
                            ),
                          },
                        });
                      }}
                    >
                      <option value={0}>0–10</option>
                      <option value={1}>1–10</option>
                    </select>
                  </Field>
                </FormGrid>
                {condition.source.questionIndex &&
                  !inferQuestionScoring(
                    questions.find((item) => item.index === condition.source.questionIndex) ?? ({} as ReportQuestion),
                  ).nps && <Hint>{t('definition.segment.npsNotDetected')}</Hint>}
                <Hint>{t('definition.segment.bucketsHelp')}</Hint>
                {BUCKETS.map((bucket) => (
                  <Check key={bucket}>
                    <input
                      type="checkbox"
                      checked={condition.buckets.includes(bucket)}
                      onChange={(event) =>
                        updateCondition(index, {
                          ...condition,
                          buckets: toggle(condition.buckets, bucket, event.target.checked),
                        })
                      }
                    />
                    {t(`definition.segment.buckets.${bucket}`)}
                  </Check>
                ))}
              </>
            )}
          </SourceBlock>
        );
      })}
      {calculation.conditions.length < 10 && (
        <Bar>
          <Action onClick={() => setConditions([...calculation.conditions, newDeviationCondition(t, language)])}>
            + {t('definition.segment.addCategory')}
          </Action>
          <Action
            onClick={() =>
              setConditions([
                ...calculation.conditions,
                newNpsCondition(npsQuestions.find((item) => inferQuestionScoring(item).nps)),
              ])
            }
          >
            + {t('definition.segment.addNps')}
          </Action>
        </Bar>
      )}
      <FormGrid>
        {(['matching', 'other'] as const).map((key) => (
          <Field key={key}>
            {t(`definition.segment.labels.${key}`)} ({language})
            <input
              value={calculation.labels[key][language] ?? ''}
              onChange={(event) =>
                onChange({
                  ...calculation,
                  labels: {
                    ...calculation.labels,
                    [key]: { ...calculation.labels[key], [language]: event.target.value },
                  },
                })
              }
            />
          </Field>
        ))}
      </FormGrid>
    </>
  );
}
